import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormGroup,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { DentalMarketingPlan } from '../../../services/models/inurance-plan'
import OneStepForm, { DataEntry, FormValueType } from '../../OneStepForm'
interface DentalProductDetailsDialogProps {
    dentalProduct: DentalMarketingPlan
    open: boolean
    onClose: () => void
}

export const DentalProductDetailsDialog = (props: DentalProductDetailsDialogProps) => {
    const { dentalProduct, open, onClose } = props
    const tableCellCss = {
        fontSize: 'small',
        border: 1,
        borderColor: 'lightgrey',
    }
    const productBasicElements: DataEntry[] = [
        {
            key: 'productName',
            label: 'Product Name',
            type: FormValueType.TEXT,
            initialValue: dentalProduct?.planName,
        },
        {
            key: 'networkName',
            label: 'Product Network Identifier',
            type: FormValueType.TEXT,
            initialValue: dentalProduct?.networkName,
        },
        {
            key: 'productId',
            label: 'Product Identifier',
            type: FormValueType.TEXT,
            initialValue: dentalProduct?.planCode,
        },
        {
            key: 'planYear',
            label: 'Plan Year',
            type: FormValueType.NUMBER,
            initialValue: dentalProduct?.planYear,
        },
    ]
    const productDeductibles: DataEntry[] = [
        {
            key: 'individualDeductible',
            label: 'Deductible - Individual ($)',
            type: FormValueType.CURRENCY,
            initialValue: dentalProduct?.individualDeductibleInDollars,
        },
        {
            key: 'familyDeductible',
            label: 'Deductible - Family ($)',
            type: FormValueType.CURRENCY,
            initialValue: dentalProduct?.familyDeductibleInDollars,
        },
    ]
    if (!dentalProduct) {
        if (open) {
            onClose()
        }
        return <></>
    }

    return (
        <Dialog open={open} maxWidth="lg">
            <DialogTitle>View Product</DialogTitle>
            <DialogContent>
                <>
                    <FormControl sx={{ width: '100%' }} component="fieldset" variant="standard">
                        <FormGroup
                            sx={{
                                flexFlow: 'row wrap',
                                justifyContent: 'space-between',
                                gap: 2,
                                padding: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <OneStepForm formData={productBasicElements} controlled />
                                <Divider sx={{ margin: 1, marginTop: 3, marginBottom: 3 }} />
                                <OneStepForm formData={productDeductibles} controlled />
                                <Divider sx={{ margin: 1, marginTop: 3, marginBottom: 3 }} />
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...tableCellCss, width: '25%' }}>
                                                Covered Dental Services
                                            </TableCell>
                                            <TableCell sx={tableCellCss}>
                                                {`Year ${dentalProduct.benefitPeriod[0].year}`}
                                            </TableCell>
                                            <TableCell sx={tableCellCss}>
                                                {`Year ${dentalProduct.benefitPeriod[1].year}`}
                                            </TableCell>
                                            <TableCell sx={tableCellCss}>
                                                {`Year ${dentalProduct.benefitPeriod[2].year}`}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ ...tableCellCss, width: '25%' }}>
                                                Benefit Year Maximum Benefits
                                            </TableCell>
                                            {dentalProduct.benefitPeriod.map((entry) => (
                                                <TableCell sx={tableCellCss}>
                                                    {`$${entry.annualBenefitMaximumInDollars}`}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ ...tableCellCss, width: '25%' }}>
                                                Diagnostic & Preventive Services
                                                <Typography sx={{ fontSize: 'small' }}>
                                                    Oral examinations and cleanings (prophylaxis),
                                                    X-rays, Fluoride and Sealants
                                                </Typography>
                                            </TableCell>
                                            {dentalProduct.benefitPeriod.map((entry) => (
                                                <TableCell sx={tableCellCss}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 'small',
                                                        }}
                                                    >
                                                        {`${entry.preventiveServicesInNetworkPercentage}% In-Network`}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 'small',
                                                        }}
                                                    >
                                                        {`${entry.preventiveServicesOutOfNetworkPercentage}% Out-of-Network`}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ ...tableCellCss, width: '25%' }}>
                                                Basic Services
                                                <Typography sx={{ fontSize: 'small' }}>
                                                    Fillings and Extractions
                                                </Typography>
                                            </TableCell>
                                            {dentalProduct.benefitPeriod.map((entry) => (
                                                <TableCell sx={tableCellCss}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 'small',
                                                        }}
                                                    >
                                                        {`${entry.basicServicesInNetworkPercentage}% In-Network`}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 'small',
                                                        }}
                                                    >
                                                        {`${entry.basicServicesOutOfNetworkPercentage}% Out-of-Network`}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ ...tableCellCss, width: '25%' }}>
                                                Major Services
                                                <Typography sx={{ fontSize: 'small' }}>
                                                    Bleaching and Whitening, Endodontics,
                                                    Periodontics, Oral Surgery, Prosthodontic
                                                    Services, Crowns, Implants, Veneers and Dentures
                                                </Typography>
                                            </TableCell>
                                            {dentalProduct.benefitPeriod.map((entry) => (
                                                <TableCell sx={tableCellCss}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 'small',
                                                        }}
                                                    >
                                                        {`${entry.majorServicesInNetworkPercentage}% In-Network`}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 'small',
                                                        }}
                                                    >
                                                        {`${entry.majorServicesOutOfNetworkPercentage}% Out-of-Network`}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ ...tableCellCss, width: '25%' }}>
                                                Orthodontia
                                                <Typography sx={{ fontSize: 'small' }}>
                                                    All eligible enrollees. A separate lifetime
                                                    maximum of $1,000 per eligible enrollee applies
                                                    to orthodontic benefits
                                                </Typography>
                                            </TableCell>
                                            {dentalProduct.benefitPeriod.map((entry) => (
                                                <TableCell sx={tableCellCss}>
                                                    {entry.orthodontiaInNetworkPercentage &&
                                                    entry.orthodontiaOutOfNetworkPercentage ? (
                                                        <>
                                                            <Typography sx={{ fontSize: 'small' }}>
                                                                {`${entry.orthodontiaInNetworkPercentage}% In-Network`}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 'small' }}>
                                                                {`${entry.orthodontiaOutOfNetworkPercentage}% Out-of-Network`}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography sx={{ fontSize: 'small' }}>
                                                            Not Included
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                </Table>
                            </Box>
                        </FormGroup>
                    </FormControl>
                </>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button>Verify</Button>
                <Button>Publish</Button>
            </DialogActions>
        </Dialog>
    )
}
